/**
 * FollowUpRows.js
 *
 * Presentational building blocks of the Teendők screen: tab chip, action button, follow-up row
 * (tap to expand the action bar) and calendar-event row. Pure UI — all behaviour arrives via props.
 *
 * @module components/management/FollowUpRows
 */

import React, {useEffect, useRef} from 'react';
import {Animated, Linking, StyleSheet, Text, TouchableOpacity, View} from 'react-native';
import {useTranslation} from 'react-i18next';
import Icon from './SafeIcon';
import {StatusBadge} from './ManagementUI';
import {IconChip} from '../premium/PremiumKit';
import {useManagementTheme} from '../../helpers/managementTheme';
import {countdownText, entityOf, eventSubtitle, plainText} from '../../helpers/salesFollowUpApi';
import {CONTACT_ORANGE, FOLLOW_UP_ICONS, followUpTypeLabel} from '../../config/leadContactMeta';

const fmtTime = (d) => (d ? new Date(d).toLocaleTimeString([], {hour: '2-digit', minute: '2-digit'}) : '');

/**
 * Tab chip with a count bubble.
 * @param {{label: string, count: number, active: boolean, tone?: string, onPress: () => void}} props props.
 * @returns {JSX.Element} chip.
 */
export const TabChip = ({label, count, active, tone, onPress}) => {
    const m = useManagementTheme();

    return (
        <TouchableOpacity
            activeOpacity={0.85}
            onPress={onPress}
            accessibilityRole="tab"
            accessibilityState={{selected: !!active}}
            style={[styles.tab, {borderColor: active ? m.colors.primary : m.colors.border, backgroundColor: active ? m.colors.chipBg : m.colors.surface}]}
        >
            <Text style={[styles.tabText, {color: active ? m.colors.primary : m.colors.subText}]} numberOfLines={1}>{label}</Text>
            {count > 0 && (
                <View style={[styles.tabCount, {backgroundColor: tone || (active ? m.colors.primary : m.colors.muted)}]}>
                    <Text style={styles.tabCountText}>{count > 99 ? '99+' : count}</Text>
                </View>
            )}
        </TouchableOpacity>
    );
};

/**
 * Compact action button (outlined, or filled with `filled`).
 * @param {{icon: string, label: string, onPress: () => void, tone?: string, filled?: boolean}} props props.
 * @returns {JSX.Element} button.
 */
export const ActionBtn = ({icon, label, onPress, tone, filled}) => {
    const m = useManagementTheme();
    const color = tone || m.colors.primary;

    return (
        <TouchableOpacity
            activeOpacity={0.7}
            onPress={onPress}
            accessibilityRole="button"
            style={[styles.actionBtn, filled ? {backgroundColor: color, borderColor: color} : {borderColor: m.colors.border, backgroundColor: m.colors.surfaceAlt}]}
        >
            <Icon name={icon} size={17} color={filled ? '#FFFFFF' : color} />
            <Text style={[styles.actionText, {color: filled ? '#FFFFFF' : m.colors.heading}]} numberOfLines={1}>{label}</Text>
        </TouchableOpacity>
    );
};

/**
 * One follow-up row; tap to expand the action bar.
 * @param {object} props item, expanded, highlighted, readOnly, onToggle, actions ({done, call, snooze, reschedule, open, cancel}),
 *   optional `rowRef` (ref of the outer view, for scroll-to) and `pulse` (brief orange flash when it becomes true).
 * @returns {JSX.Element} row.
 */
export const FollowUpRow = ({item, expanded, highlighted, readOnly, onToggle, actions, rowRef, pulse}) => {
    const {t} = useTranslation();
    const m = useManagementTheme();
    const e = entityOf(item);
    const overdue = !!item.isOverdue;
    const flash = useRef(new Animated.Value(0)).current;

    useEffect(() => {
        if (pulse) {
            Animated.sequence([
                Animated.timing(flash, {toValue: 1, duration: 280, useNativeDriver: true}),
                Animated.timing(flash, {toValue: 0, duration: 380, useNativeDriver: true}),
                Animated.timing(flash, {toValue: 1, duration: 280, useNativeDriver: true}),
                Animated.timing(flash, {toValue: 0, duration: 600, useNativeDriver: true}),
            ]).start();
        }
    }, [pulse, flash]);

    return (
        <View ref={rowRef} collapsable={false} style={[styles.row, {backgroundColor: m.colors.surface, borderColor: highlighted ? m.colors.primary : m.colors.border, borderWidth: highlighted ? 2 : 1}, m.shadow(1)]}>
            <Animated.View pointerEvents="none" style={[StyleSheet.absoluteFillObject, {backgroundColor: m.colors.primary, opacity: flash.interpolate({inputRange: [0, 1], outputRange: [0, 0.22]})}]} />
            <TouchableOpacity activeOpacity={0.85} onPress={onToggle} style={styles.rowMain}>
                <View style={styles.timeCol}>
                    <Text style={[styles.timeText, {color: overdue ? m.colors.danger : m.colors.heading}]}>{fmtTime(item.dueAt)}</Text>
                    <IconChip icon={FOLLOW_UP_ICONS[item.type] || 'dots-horizontal'} tone={overdue ? 'danger' : 'primary'} size={34} round />
                </View>
                <View style={{flex: 1}}>
                    <Text style={[styles.rowTitle, {color: m.colors.heading}]} numberOfLines={1}>{e.name || item.title || '—'}</Text>
                    {!!e.company && <Text style={[styles.rowSub, {color: m.colors.subText}]} numberOfLines={1}>{e.company}</Text>}
                    {!!item.note && <Text style={[styles.rowNote, {color: m.colors.subText}]} numberOfLines={2}>{item.note}</Text>}
                    <View style={styles.badgeRow}>
                        <StatusBadge label={followUpTypeLabel(t, item.type)} tone="neutral" />
                        {overdue && <StatusBadge label={countdownText(item.dueAt, t)} color="#EF4444" />}
                        {!!item.requestedByClient && <StatusBadge label={t('sales.fu.requested', {defaultValue: 'Ügyfél kérte'})} icon="account-voice" color={CONTACT_ORANGE} />}
                        {readOnly && !!item.completedAt && <StatusBadge label={fmtTime(item.completedAt)} icon="check" tone="positive" />}
                    </View>
                </View>
                {!readOnly && <Icon name={expanded ? 'chevron-up' : 'chevron-down'} size={20} color={m.colors.muted} />}
            </TouchableOpacity>
            {expanded && !readOnly && (
                <View style={[styles.actionBar, {borderTopColor: m.colors.border}]}>
                    <ActionBtn icon="check-circle-outline" label={t('sales.fu.act_done', {defaultValue: 'Kész'})} tone="#16A34A" filled onPress={() => actions.done(item)} />
                    {!!e.phone && <ActionBtn icon="phone-outline" label={t('sales.fu.act_call', {defaultValue: 'Hívás'})} onPress={() => actions.call(item)} />}
                    <ActionBtn icon="clock-outline" label={t('sales.fu.act_snooze', {defaultValue: 'Halasztás'})} onPress={() => actions.snooze(item)} />
                    <ActionBtn icon="calendar-edit" label={t('sales.fu.act_resched', {defaultValue: 'Átütemezés'})} onPress={() => actions.reschedule(item)} />
                    <ActionBtn icon="open-in-new" label={t('sales.fu.act_open', {defaultValue: 'Megnyitás'})} onPress={() => actions.open(item)} />
                    <ActionBtn icon="close-circle-outline" label={t('sales.fu.act_cancel', {defaultValue: 'Lemondás'})} tone="#EF4444" onPress={() => actions.cancel(item)} />
                </View>
            )}
        </View>
    );
};

/**
 * Calendar event card: time block (start / end or "all day"), title, readable subtitle (location, linked lead,
 * organizer — object fields are resolved to text, never "[object Object]") and a "Csatlakozás" button when the
 * event has a meeting URL.
 * @param {{event: object}} props props.
 * @returns {JSX.Element} card.
 */
export const EventRow = ({event}) => {
    const {t} = useTranslation();
    const m = useManagementTheme();
    const subtitle = eventSubtitle(event);
    const title = plainText(event.title) || '—';
    const url = typeof event.meetingUrl === 'string' ? event.meetingUrl : '';

    return (
        <View style={[styles.row, styles.eventRow, {backgroundColor: m.colors.surface, borderColor: m.colors.border}, m.shadow(1)]}>
            <View style={[styles.eventTime, {backgroundColor: m.colors.chipBg}]}>
                {event.allDay ? (
                    <Icon name="calendar-today" size={20} color={m.colors.primary} />
                ) : (
                    <>
                        <Text style={[styles.eventTimeMain, {color: m.colors.primary}]}>{fmtTime(event.start)}</Text>
                        {!!event.end && <Text style={[styles.eventTimeEnd, {color: m.colors.subText}]}>{fmtTime(event.end)}</Text>}
                    </>
                )}
            </View>
            <View style={{flex: 1}}>
                <Text style={[styles.rowTitle, {color: m.colors.heading}]} numberOfLines={2}>{title}</Text>
                {event.allDay && <Text style={[styles.rowSub, {color: m.colors.subText}]}>{t('sfu_all_day')}</Text>}
                {!!subtitle && <Text style={[styles.rowSub, {color: m.colors.subText}]} numberOfLines={1}>{subtitle}</Text>}
            </View>
            {!!url && (
                <ActionBtn icon="video-outline" label={t('sfu_join')} tone={m.colors.info} filled onPress={() => Linking.openURL(url).catch(() => {})} />
            )}
        </View>
    );
};

const styles = StyleSheet.create({
    loader: {paddingVertical: 48, alignItems: 'center', justifyContent: 'center'},
    progressWrap: {marginTop: 14},
    progressTrack: {height: 8, borderRadius: 999, backgroundColor: 'rgba(255,255,255,0.18)', overflow: 'hidden'},
    progressFill: {height: 8, borderRadius: 999, backgroundColor: CONTACT_ORANGE},
    progressText: {color: '#E2E8F0', fontSize: 12, fontWeight: '800', marginTop: 6},
    nextTop: {flexDirection: 'row', alignItems: 'center', gap: 12},
    nextTime: {fontSize: 30, fontWeight: '900'},
    countdown: {fontSize: 12.5, fontWeight: '800', marginTop: 2},
    nextActions: {flexDirection: 'row', gap: 8, marginTop: 14},
    tabsRow: {flexDirection: 'row', gap: 8, marginTop: 16, marginBottom: 10},
    tab: {flex: 1, flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 6, borderRadius: 999, borderWidth: 1.2, paddingVertical: 10, paddingHorizontal: 4},
    tabText: {fontSize: 12.5, fontWeight: '800'},
    tabCount: {minWidth: 20, height: 20, borderRadius: 10, alignItems: 'center', justifyContent: 'center', paddingHorizontal: 5},
    tabCountText: {color: '#FFFFFF', fontSize: 11, fontWeight: '900'},
    segment: {flexDirection: 'row', borderWidth: 1, borderRadius: 14, padding: 3, marginBottom: 6, alignSelf: 'flex-start'},
    segmentItem: {borderRadius: 11, paddingHorizontal: 14, paddingVertical: 7},
    segmentText: {fontSize: 12.5, fontWeight: '800'},
    daysRow: {flexDirection: 'row', gap: 8, marginBottom: 6},
    dayChip: {borderRadius: 999, borderWidth: 1.4, paddingHorizontal: 14, paddingVertical: 7},
    row: {borderRadius: 24, borderWidth: 1, marginBottom: 10, overflow: 'hidden'},
    eventRow: {flexDirection: 'row', alignItems: 'center', gap: 12, padding: 12},
    eventTime: {minWidth: 58, borderRadius: 16, paddingVertical: 8, paddingHorizontal: 8, alignItems: 'center', justifyContent: 'center'},
    eventTimeMain: {fontSize: 15, fontWeight: '900', letterSpacing: -0.2},
    eventTimeEnd: {fontSize: 11.5, fontWeight: '700', marginTop: 1},
    rowMain: {flexDirection: 'row', alignItems: 'center', gap: 12, padding: 12},
    timeCol: {alignItems: 'center', gap: 6, minWidth: 54},
    timeText: {fontSize: 15, fontWeight: '800', letterSpacing: -0.2},
    typeChip: {width: 32, height: 32, borderRadius: 16, alignItems: 'center', justifyContent: 'center'},
    rowTitle: {fontSize: 15.5, fontWeight: '800'},
    rowSub: {fontSize: 12.5, fontWeight: '700', marginTop: 1},
    rowNote: {fontSize: 12.5, fontWeight: '600', marginTop: 3},
    badgeRow: {flexDirection: 'row', flexWrap: 'wrap', gap: 6, marginTop: 6},
    actionBar: {flexDirection: 'row', flexWrap: 'wrap', gap: 8, padding: 12, borderTopWidth: 1},
    actionBtn: {flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 6, borderRadius: 999, borderWidth: 1, paddingHorizontal: 14, paddingVertical: 10, flexGrow: 1},
    actionText: {fontSize: 12.5, fontWeight: '800'},
    statRow: {flexDirection: 'row', gap: 8},
    statChip: {flex: 1, borderWidth: 1, borderRadius: 14, paddingVertical: 10, paddingHorizontal: 6, alignItems: 'center'},
    statValue: {fontSize: 20, fontWeight: '900'},
    statLabel: {fontSize: 10.5, fontWeight: '800', textAlign: 'center', marginTop: 2},
    mailRow: {flexDirection: 'row', gap: 10, marginTop: 12, alignItems: 'flex-start'},
    prioDot: {width: 10, height: 10, borderRadius: 5, marginTop: 5},
    aiBox: {borderWidth: 1.4, borderRadius: 14, padding: 12, marginTop: 12, gap: 4},
    optRow: {flexDirection: 'row', alignItems: 'center', borderWidth: 1, borderRadius: 18, padding: 14, marginTop: 14},
});

/** Shared style sheet of the Teendők screen (rows + screen-level blocks). */
export const followUpRowStyles = styles;
